import axios from "../../helpers/bearerAxios";
import { logger } from "../loggingService";
import type { OneButtonUserCapabilityResponse } from "../../@types/oneButtonCapability";

export type OneButtonCapabilityValue = '' | 'View' | 'Edit' | 'Yes' | string;

type CapabilityItemLike = { capability_name?: unknown; capability_value?: unknown };

const isCapabilityItem = (v: unknown): v is CapabilityItemLike => {
    return !!v && typeof v === 'object' && 'capability_name' in (v as any);
};

/**
 * Reads a capability value (e.g. 'View'/'Edit') from the oneBUTTON capability payload.
 * This mirrors the Angular `getCapability(name)` behavior used in oneBUTTONDashboard.
 */
export const getOneButtonCapabilityValue = (
    payload: OneButtonUserCapabilityResponse | null | undefined,
    name: string
): OneButtonCapabilityValue => {
    const caps = payload?.capabilities;
    if (!caps) return '';

    const tryFindInUnknown = (v: unknown): OneButtonCapabilityValue | null => {
        if (!v) return null;

        // Common case: array of capability items
        if (Array.isArray(v)) {
            for (const item of v) {
                if (isCapabilityItem(item) && String(item.capability_name) === name) {
                    return String((item as any).capability_value || '');
                }
            }
            return null;
        }

        // Sometimes a single capability item object
        if (isCapabilityItem(v) && String((v as any).capability_name) === name) {
            return String((v as any).capability_value || '');
        }

        // Or a nested object whose values may contain arrays/items
        if (typeof v === 'object') {
            for (const child of Object.values(v as Record<string, unknown>)) {
                const found = tryFindInUnknown(child);
                if (found !== null) return found;
            }
        }

        return null;
    };

    for (const group of Object.values(caps as Record<string, unknown>)) {
        const found = tryFindInUnknown(group);
        if (found !== null) return found;
    }
    return '';
};

/**
 * Angular-style “show control” for raw `getCapability` strings (*ngIf truthy / auth View|Edit|Yes|True).
 * Use only where parity with Trinity grids is needed — do not route global `hasOneButtonCapability` through this
 * (that helper stays strict exact-match so the rest of the app is unchanged).
 */
export const oneButtonCapabilityRawAllows = (raw: string | OneButtonCapabilityValue): boolean => {
    const u = String(raw ?? '').trim().toLowerCase();
    return u === 'view' || u === 'edit' || u === 'yes' || u === 'true';
};

/**
 * Convenience helper to interpret oneBUTTON capability values as a boolean "has access".
 */
export const hasOneButtonCapability = (
    payload: OneButtonUserCapabilityResponse | null | undefined,
    name: string
): boolean => {
    const v = getOneButtonCapabilityValue(payload, name);
    return v === 'View' || v === 'Edit' || v === 'Yes';
};

/**
 * Coarse finance-partner visibility: same capability names and display strings as Angular
 * `AppComponent.updateCapability()` → `authService.setFinancePartnersCapability(...)`.
 * Only entries with capability_value === 'View' are collected (Angular checks View).
 */
export const COARSE_FINANCE_PARTNER_CAPABILITIES: ReadonlyArray<{
    capabilityName: string;
    partnerDisplayText: string;
}> = [
    { capabilityName: 'Iscash', partnerDisplayText: 'Cash' },
    { capabilityName: 'IsServiceFinance', partnerDisplayText: 'Service Finance' },
    { capabilityName: 'IsSunnova', partnerDisplayText: 'Sunnova' },
    { capabilityName: 'IsIGS_Solar', partnerDisplayText: 'IGS' },
    { capabilityName: 'isGoodLeap', partnerDisplayText: 'Good Leap' },
    { capabilityName: 'isSunlight', partnerDisplayText: 'Sunlight' },
    { capabilityName: 'isLightreach', partnerDisplayText: 'Lightreach' },
    { capabilityName: 'isEnfin', partnerDisplayText: "Enfin" }
];

/**
 * Builds the allow-list of finance partner **display names** (`ele.Text` from GetMasterDataByStep)
 * matching Angular `authService.capabilityFinancePartners` after login.
 */
export const buildCapabilityFinancePartnerDisplayNames = (
    payload: OneButtonUserCapabilityResponse | null | undefined
): string[] => {
    const out: string[] = [];
    for (const row of COARSE_FINANCE_PARTNER_CAPABILITIES) {
        if (getOneButtonCapabilityValue(payload, row.capabilityName) === 'View') {
            out.push(row.partnerDisplayText);
        }
    }
    return out;
};

/**
 * Intersects master `FinancePartnerName` rows with the coarse allow-list (Angular
 * `opportunity-info.component.ts` getMasterData loop).
 */
export const filterFinancePartnerMasterListByAllowList = <T extends { Text?: string }>(
    financePartnerName: T[] | undefined,
    allowedDisplayTexts: string[]
): T[] => {
    if (!Array.isArray(financePartnerName)) return [];
    if (allowedDisplayTexts.length === 0) return [];
    const allow = new Set(allowedDisplayTexts);
    return financePartnerName.filter((ele) => ele?.Text != null && allow.has(String(ele.Text)));
};

const getUserCapability = async (): Promise<OneButtonUserCapabilityResponse> => {
    try {
        const url = import.meta.env.VITE_ONEBUTTON_GET_USER_CAPABILITY_URL;

        const response = await axios.get(url);
        return response.data;
    } catch (e: any) {
        logger.error(e);
        throw e;
    }
};

const oneButtonCapabilityService = {
    getUserCapability,
};

export default oneButtonCapabilityService;

